import { useState, useRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import ProviderLayout from "@/layouts/ProviderLayout";
import { useAddDetailedProvider, useListMasterCptCodes, useGetCredentialTypes, useGetLanguages, useGetProviderTypes, useGetVisitTypes } from "@/lib/api-client-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "wouter";
import {
  Heart, User, Briefcase, FileText, MapPin, Globe,
  ChevronRight, ChevronLeft, Plus, Trash2, Check,
  ChevronDown, Building2, Phone, Mail,
  GraduationCap, Award, Stethoscope, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const SALUTATIONS = ["Dr.", "Mr.", "Mrs.", "Ms.", "Prof.", "Rev.", "Other"];
const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

/* ─── Types ──────────────────────────────────────────────────────────────── */

type Credential = {
  type: string; title: string; institution: string; year: string; description: string;
};
type Schedule = {
  dayOfWeek: string; startTime: string; endTime: string; isClosed: boolean;
};
const DEFAULT_SCHEDULES: Schedule[] = [
  { dayOfWeek: "Monday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Tuesday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Wednesday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Thursday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Friday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Saturday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: true },
  { dayOfWeek: "Sunday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: true },
];

type Location = {
  isPrimary: boolean; buildingNo: string; address1: string; address2: string; city: string;
  state: string; zipCode: string; phone: string; officePhone: string;
  fax: string; email: string; website: string; schedules: Schedule[];
};
type FormData = {
  practiceType: "individual" | "group";
  salutation: string; firstName: string; middleName: string; lastName: string;
  displayName: string; gender: string; headline: string;
  npiNumber: string; individualNpiNumber?: string; groupNpiNumber?: string; affiliatedName?: string; providerTypes: string[]; languages: string[];
  statesLicensed: string[]; credentials: Credential[]; medicareCharge: string;
  synopsis: string; visitTypes: string[];
  locations: Location[];
  zocdocUrl: string; webmdUrl: string; healthgradesUrl: string; usnewsUrl: string;
  email: string;
  phone: string;
  cptCodes: string[];
};

/* ─── MultiSelect ────────────────────────────────────────────────────────── */

function MultiSelect({
  options = [], value = [], onChange, placeholder, maxHeight = 260,
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; maxHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = (options || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      return { value: val, label: lbl };
    }
    return { value: String(o ?? ""), label: String(o ?? "") };
  });

  const filtered = normalizedOptions.filter(o =>
    o.label.toLowerCase().includes((search || "").toLowerCase())
  );

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    onChange(safeValue.includes(optValue) ? safeValue.filter(v => v !== optValue) : [...safeValue, optValue]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[40px] px-3 py-2 border border-input rounded-md bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors"
        >
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            safeValue.map(v => {
              const label = normalizedOptions.find(o => o.value === v)?.label || v;
              return (
                <Badge key={v} variant="secondary" className="text-xs rounded-full px-2 py-0.5">
                  {label}
                  <span
                    className="ml-1 cursor-pointer hover:text-destructive"
                    onClick={e => { e.stopPropagation(); toggle(v); }}
                  >×</span>
                </Badge>
              );
            })
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No results</p>
          ) : filtered.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
            >
              <div className={cn(
                "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                safeValue.includes(opt.value) ? "bg-primary border-primary" : "border-input"
              )}>
                {safeValue.includes(opt.value) && <Check className="w-3 h-3 text-white" />}
              </div>
              {opt.label}
            </button>
          ))}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t">
            <button type="button" onClick={() => onChange([])} className="text-xs text-muted-foreground hover:text-foreground">
              Clear all ({value.length} selected)
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/* ─── Step Indicator ─────────────────────────────────────────────────────── */

const STEPS = [
  { label: "Personal", icon: User },
  { label: "Professional", icon: Briefcase },
  { label: "About", icon: FileText },
  { label: "Locations", icon: MapPin },
  { label: "Online Presence", icon: Globe },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                done ? "bg-primary border-primary text-white"
                  : active ? "bg-white border-primary text-primary shadow-md shadow-primary/20"
                    : "bg-white border-border text-muted-foreground"
              )}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={cn(
                "text-[11px] font-medium whitespace-nowrap hidden sm:block",
                active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"
              )}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={cn(
                "w-10 sm:w-16 h-0.5 mb-5 transition-all duration-500",
                i < current ? "bg-primary" : "bg-border"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Field helpers ──────────────────────────────────────────────────────── */

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

export default function AddProvider() {
  const [step, setStep] = useState(0);
  const [, setLocation] = useLocation();

  const { data: languagesData = [] } = useGetLanguages();
  const { data: providerTypesData = [] } = useGetProviderTypes();
  const { data: visitTypesData = [] } = useGetVisitTypes();
  const { data: credentialTypesData = [] } = useGetCredentialTypes();

  const LANGUAGES = languagesData.map((l: any) => ({ value: l.id.toString(), label: l.name }));
  const PROVIDER_TYPES = providerTypesData.map((p: any) => ({ value: p.id.toString(), label: p.name }));
  const VISIT_TYPES_OPTIONS = visitTypesData.map((v: any) => ({ value: v.id.toString(), label: v.name }));
  const CREDENTIAL_TYPES = credentialTypesData.map((c: any) => ({ value: c.id.toString(), label: c.name }));
  const US_STATES_OPTIONS = US_STATES.map(s => ({ value: s, label: s }));

  const TIME_OPTIONS = Array.from({ length: 48 }).map((_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? '00' : '30';
    const ampm = h < 12 ? 'AM' : 'PM';
    const hour = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${hour.toString().padStart(2, '0')}:${m} ${ampm}`;
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [isDisplayNameManuallyEdited] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const form = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      email: "", phone: "", cptCodes: [],
      practiceType: "group",
      salutation: "", firstName: "", middleName: "", lastName: "",
      displayName: "", gender: "", headline: "",
      npiNumber: "", providerTypes: [], languages: [],
      statesLicensed: [], credentials: [], medicareCharge: "",
      synopsis: "", visitTypes: [],
      locations: [{
        isPrimary: true, buildingNo: "", address1: "", address2: "", city: "", state: "",
        zipCode: "", phone: "", officePhone: "", fax: "", email: "", website: "", schedules: [...DEFAULT_SCHEDULES],
      }],
      zocdocUrl: "", webmdUrl: "", healthgradesUrl: "", usnewsUrl: "",
    },
  });

  const { register, control, trigger, watch, setValue, getValues, formState: { errors } } = form;

  const { fields: credFields, append: addCred, remove: removeCred } = useFieldArray({ control, name: "credentials" });
  const { fields: locFields, append: addLoc, remove: removeLoc } = useFieldArray({ control, name: "locations" });

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const STEP_FIELDS: (keyof FormData)[][] = [
    ["firstName", "lastName", "displayName"],
    ["npiNumber", "medicareCharge"],
    [],
    [],
    [],
  ];

  const isStepValid = () => {
    const fields = STEP_FIELDS[step];
    if (!fields || fields.length === 0) return true;

    // Check if any of the fields in this step have errors
    const hasErrors = fields.some(field => errors[field]);
    if (hasErrors) return false;

    // Check if all required fields have a value
    const values = watch(fields as any);
    if (!values) return false;
    if (Array.isArray(values)) {
      return values.every(v => v !== undefined && v !== null && v.toString().trim().length > 0);
    }
    return values !== undefined && values !== null && values.toString().trim().length > 0;
  };

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;
    setStep(s => s + 1);
    scrollTop();
  };

  const goBack = () => { setStep(s => s - 1); scrollTop(); };

  const addProvider = useAddDetailedProvider();
  const { data: masterCptCodesData } = useListMasterCptCodes();
  const masterCptCodes = masterCptCodesData?.items || [];

  const onSubmit = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;

    setLoading(true);
    try {
      const vals = getValues();
      await addProvider.mutateAsync(vals);

      setDone(true);
    } catch (err: any) {
      alert(err.message || "Failed to add provider");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <ProviderLayout>
        <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 flex flex-col items-center justify-center mt-12 bg-white rounded-2xl shadow-sm border">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-emerald-50 text-emerald-600 p-6 rounded-full mb-6">
            <Check className="w-16 h-16" />
          </motion.div>
          <h2 className="text-3xl font-bold mb-3">Successfully Added!</h2>
          <p className="text-muted-foreground text-center text-lg mb-8">
            The provider profile is complete and has been added.
          </p>
          <Button onClick={() => { setDone(false); setStep(0); form.reset(); }} size="lg" className="rounded-xl px-8">
            Add Another Provider <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 min-h-screen grid lg:grid-cols-2" ref={topRef}>
        <div className="hidden lg:flex flex-col bg-gradient-to-br from-secondary via-secondary/90 to-primary p-12 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_60%)]" />
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl">StraightDoc</span>
          </div>
          <div className="flex-1 flex flex-col justify-center relative z-10">
            <motion.h1 initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="text-4xl font-bold mb-4">
              Join the future of healthcare
            </motion.h1>
            <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="text-white/70 text-lg mb-8">
              Create your free account to access verified providers, instant cost estimates, and seamless care coordination.
            </motion.p>
            <div className="bg-white/10 rounded-2xl p-5 backdrop-blur border border-white/20">
              <p className="text-sm font-medium mb-4">Why choose StraightDoc?</p>
              {["Passwordless secure access", "Find top-rated professionals", "Real-time cost estimates", "HIPAA-secure environment"].map((item, i) => (
                <motion.div key={item} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-3 text-white/90 mb-3 last:mb-0">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col p-6 sm:p-10 bg-background relative overflow-y-auto">
          <div className="w-full max-w-xl mx-auto relative z-10 my-auto pt-4 pb-12">
            {/* Mobile Header */}
            <div className="flex items-center justify-between mb-8 lg:hidden">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg">StraightDoc</span>
              </div>
              <Link href="/login" className="text-sm text-primary font-medium hover:underline">Sign in</Link>
            </div>
            {/* Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-medium px-3 py-1.5 rounded-full mb-3">
                <Stethoscope className="w-4 h-4" />
                Provider Registration
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">Join the StraightDoc Network</h1>
              <p className="text-muted-foreground">Complete your professional profile in 5 simple steps</p>
            </div>

            {/* Step Indicator */}
            <StepIndicator current={step} />

            {/* Form Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.22 }}
                className="bg-white rounded-2xl border border-border shadow-sm p-6 sm:p-8"
              >
                {/* ── Step 0: Personal Info ── */}
                {step === 0 && (
                  <div className="space-y-5">
                    <SectionHeader icon={User} title="Personal Information" subtitle="Your name and basic personal details" />
                    <div className="space-y-1">
                      <FieldLabel>Email</FieldLabel>
                      <Input placeholder="provider@example.com" {...register("email", { required: "Email is required" })} />
                      {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Phone</FieldLabel>
                      <Input placeholder="(555) 123-4567" {...register("phone")} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel>Salutation</FieldLabel>
                        <Controller
                          control={control} name="salutation"
                          render={({ field }) => (
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                      <div>
                        <FieldLabel required>First Name</FieldLabel>
                        <Input
                          {...register("firstName", {
                            required: "First name is required",
                            onChange: (e) => {
                              if (!isDisplayNameManuallyEdited) {
                                const vals = getValues();
                                const newFirst = e.target.value;
                                setValue("displayName", `${newFirst} ${vals.lastName}`.trim(), { shouldValidate: true });
                              }
                            }
                          })}
                          placeholder="Jane" className="mt-1.5"
                        />
                        <FieldError message={errors.firstName?.message} />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel>Middle Name</FieldLabel>
                        <Input {...register("middleName")} placeholder="M." className="mt-1.5" />
                      </div>
                      <div>
                        <FieldLabel required>Last Name</FieldLabel>
                        <Input
                          {...register("lastName", {
                            required: "Last name is required",
                            onChange: (e) => {
                              if (!isDisplayNameManuallyEdited) {
                                const vals = getValues();
                                const newLast = e.target.value;
                                setValue("displayName", `${vals.firstName} ${newLast}`.trim(), { shouldValidate: true });
                              }
                            }
                          })}
                          placeholder="Doe" className="mt-1.5"
                        />
                        <FieldError message={errors.lastName?.message} />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel required>Display Name</FieldLabel>
                        <Input
                          {...register("displayName", { required: "Display name is required" })}
                          placeholder="Dr. Jane Smith, MD" className="mt-1.5"
                        />
                        <FieldError message={errors.displayName?.message} />
                      </div>
                      <div>
                        <FieldLabel>Gender</FieldLabel>
                        <Controller
                          control={control} name="gender"
                          render={({ field }) => (
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select gender" /></SelectTrigger>
                              <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <FieldLabel>Professional Headline</FieldLabel>
                        <Input
                          {...register("headline")}
                          placeholder="Board-certified Cardiologist · 15+ years" className="mt-1.5"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 1: Professional Details ── */}
                {step === 1 && (
                  <div className="space-y-6">
                    <SectionHeader icon={Briefcase} title="Professional Details" subtitle="Licensing, credentials and specialties" />

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel>Individual NPI (Type 1)</FieldLabel>
                        <Input
                          {...register("individualNpiNumber", {
                            pattern: { value: /^\d{10}$/, message: "NPI must be 10 digits" },
                            onChange: (e) => {
                              const val = e.target.value;
                              if (!getValues("npiNumber")) setValue("npiNumber", val);
                            }
                          })}
                          placeholder="1847291034" maxLength={10} className="mt-1.5"
                        />
                        <FieldError message={errors.individualNpiNumber?.message} />
                      </div>
                      <div>
                        <FieldLabel>Organizational NPI (Type 2)</FieldLabel>
                        <Input
                          {...register("groupNpiNumber", {
                            pattern: { value: /^\d{10}$/, message: "NPI must be 10 digits" },
                            onChange: (e) => {
                              const val = e.target.value;
                              if (!getValues("individualNpiNumber")) setValue("npiNumber", val);
                            }
                          })}
                          placeholder="1982736450" maxLength={10} className="mt-1.5"
                        />
                        <FieldError message={errors.groupNpiNumber?.message} />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel>Affiliated Practice / Organization Name</FieldLabel>
                        <Input
                          {...register("affiliatedName")}
                          placeholder="Mount Sinai Medical Center" className="mt-1.5"
                        />
                      </div>
                      <div>
                        <FieldLabel>Facility Types</FieldLabel>
                        <div className="mt-1.5">
                          <Controller
                            control={control} name="providerTypes"
                            render={({ field }) => (
                              <MultiSelect options={PROVIDER_TYPES} value={field.value} onChange={field.onChange} placeholder="Select facility types" />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                      <div className="sm:col-span-2">
                        <FieldLabel required>% of Fee Schedule</FieldLabel>
                        <div className="relative mt-1.5 mb-2">
                          <Input
                            {...register("medicareCharge", {
                              required: "Fee Schedule percentage is required",
                              min: { value: 1, message: "Must be greater than 0" }
                            })}
                            type="number"
                            placeholder="100" className="pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">%</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          This rate will be automatically applied to all your CPT codes. You won't be able to edit this later without contacting an administrator.
                        </p>
                        <FieldError message={errors.medicareCharge?.message} />
                      </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel>Languages Spoken</FieldLabel>
                        <div className="mt-1.5">
                          <Controller
                            control={control} name="languages"
                            render={({ field }) => (
                              <MultiSelect options={LANGUAGES} value={field.value} onChange={field.onChange} placeholder="Select languages" />
                            )}
                          />
                        </div>
                      </div>
                      <div>
                        <FieldLabel>States Licensed</FieldLabel>
                        <div className="mt-1.5">
                          <Controller
                            control={control} name="statesLicensed"
                            render={({ field }) => (
                              <MultiSelect options={US_STATES_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Select states" />
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Credentials */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <FieldLabel>Credentials</FieldLabel>
                          <p className="text-xs text-muted-foreground mt-0.5">Education, work experience, awards, certifications</p>
                        </div>
                        <Button
                          type="button" variant="outline" size="sm"
                          className="rounded-lg text-xs h-8"
                          onClick={() => addCred({ type: "Education", title: "", institution: "", year: "", description: "" })}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {credFields.length === 0 && (
                          <div className="border border-dashed border-border rounded-xl p-6 text-center text-sm text-muted-foreground">
                            <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            No credentials added yet. Click "Add" to get started.
                          </div>
                        )}
                        {credFields.map((cred, i) => (
                          <div key={cred.id} className="border border-border rounded-xl p-4 bg-slate-50/50 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Award className="w-4 h-4 text-primary" />
                                <span className="text-sm font-medium">Credential #{i + 1}</span>
                              </div>
                              <button type="button" onClick={() => removeCred(i)} className="text-muted-foreground hover:text-destructive transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-3">
                              <div>
                                <FieldLabel>Type</FieldLabel>
                                <Controller
                                  control={control} name={`credentials.${i}.type`}
                                  render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                                      <SelectContent>{CREDENTIAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                  )}
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <FieldLabel>Title / Degree</FieldLabel>
                                <Input {...register(`credentials.${i}.title`)} placeholder="MD, Board Certification, Excellence Award…" className="mt-1 h-9 text-sm" />
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-3">
                              <div className="sm:col-span-2">
                                <FieldLabel>Institution / Organization</FieldLabel>
                                <Input {...register(`credentials.${i}.institution`)} placeholder="Harvard Medical School" className="mt-1 h-9 text-sm" />
                              </div>
                              <div>
                                <FieldLabel>Year</FieldLabel>
                                <Input {...register(`credentials.${i}.year`)} placeholder="2012" maxLength={4} className="mt-1 h-9 text-sm" />
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Description (optional)</FieldLabel>
                              <Textarea {...register(`credentials.${i}.description`)} placeholder="Brief description…" rows={2} className="mt-1 text-sm resize-none" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 2: About & Practice ── */}
                {step === 2 && (
                  <div className="space-y-6">
                    <SectionHeader icon={FileText} title="About & Practice" subtitle="Help patients understand your practice" />

                    <div>
                      <FieldLabel>Synopsis / About</FieldLabel>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Write a compelling bio visible to patients (200–600 characters recommended)</p>
                      <Textarea
                        {...register("synopsis")}
                        placeholder="I am a board-certified cardiologist with over 15 years of experience treating patients with heart disease, hypertension, and heart failure. My practice focuses on preventive cardiology and personalized treatment plans…"
                        rows={6}
                        className="resize-none"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{watch("synopsis").length} characters</p>
                    </div>

                    <div>
                      <FieldLabel>Visit Types Offered</FieldLabel>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Select all appointment types you offer</p>
                      <Controller
                        control={control} name="visitTypes"
                        render={({ field }) => (
                          <MultiSelect options={VISIT_TYPES_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Select visit types" />
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* ── Step 3: Locations ── */}
                {step === 3 && (
                  <div className="space-y-6">
                    <SectionHeader icon={MapPin} title="Practice Locations" subtitle="Add all locations where you see patients" />

                    <div className="space-y-4">
                      {locFields.map((loc, i) => (
                        <div key={loc.id} className="border border-border rounded-2xl overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-border">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-primary" />
                              <span className="font-medium text-sm">
                                {i === 0 ? "Primary Location" : `Location ${i + 1}`}
                              </span>
                              {i === 0 && <Badge className="text-xs bg-primary/10 text-primary border-0">Primary</Badge>}
                            </div>
                            {i > 0 && (
                              <button type="button" onClick={() => removeLoc(i)} className="text-muted-foreground hover:text-destructive transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="p-4 space-y-3">
                            <div className="grid sm:grid-cols-3 gap-3">
                              <div>
                                <FieldLabel>Building No.</FieldLabel>
                                <Input {...register(`locations.${i}.buildingNo`)} placeholder="Bldg A, Suite 400" className="mt-1.5" />
                              </div>
                              <div className="sm:col-span-2">
                                <FieldLabel>Address Line 1</FieldLabel>
                                <Input {...register(`locations.${i}.address1`)} placeholder="123 Medical Center Blvd" className="mt-1.5" />
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Address Line 2</FieldLabel>
                              <Input {...register(`locations.${i}.address2`)} placeholder="Suite 400" className="mt-1.5" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <FieldLabel>City</FieldLabel>
                                <Input {...register(`locations.${i}.city`)} placeholder="New York" className="mt-1.5" />
                              </div>
                              <div>
                                <FieldLabel>State</FieldLabel>
                                <Controller
                                  control={control} name={`locations.${i}.state`}
                                  render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                                      <SelectContent className="max-h-48">{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                  )}
                                />
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <FieldLabel>ZIP Code</FieldLabel>
                                <Input {...register(`locations.${i}.zipCode`)} placeholder="10001" maxLength={10} className="mt-1.5" />
                              </div>
                              <div>
                                <FieldLabel>Location Phone</FieldLabel>
                                <div className="relative mt-1.5">
                                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                  <Input
                                    {...register(`locations.${i}.phone`, {
                                      pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                                    })}
                                    onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                                    type="tel" placeholder="(212) 555-0100" className="pl-9"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <FieldLabel>Office Phone</FieldLabel>
                                <div className="relative mt-1.5">
                                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                  <Input
                                    {...register(`locations.${i}.officePhone`, {
                                      pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                                    })}
                                    onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                                    type="tel" placeholder="(212) 555-0200" className="pl-9"
                                  />
                                </div>
                              </div>
                              <div>
                                <FieldLabel>Fax</FieldLabel>
                                <Input
                                  {...register(`locations.${i}.fax`, {
                                    pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                                  })}
                                  onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                                  type="tel" placeholder="(212) 555-0300" className="mt-1.5"
                                />
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <FieldLabel>Location Email</FieldLabel>
                                <div className="relative mt-1.5">
                                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                  <Input {...register(`locations.${i}.email`)} type="email" placeholder="office@practice.com" className="pl-9" />
                                </div>
                              </div>
                              <div>
                                <FieldLabel>Website</FieldLabel>
                                <Input {...register(`locations.${i}.website`)} placeholder="https://drjanesmith.com" className="mt-1.5" />
                              </div>
                            </div>
                            <div className="mt-4 border-t pt-4">
                              <div className="mb-3 block">
                                <FieldLabel>Office Hours</FieldLabel>
                              </div>
                              <div className="space-y-3">
                                {loc.schedules.map((schedule, dayIndex) => (
                                  <div key={schedule.dayOfWeek} className="flex items-center gap-4 bg-white p-3 rounded-xl border shadow-sm">
                                    <div className="w-24">
                                      <span className="text-sm font-medium">{schedule.dayOfWeek}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Controller
                                        control={control} name={`locations.${i}.schedules.${dayIndex}.isClosed`}
                                        render={({ field }) => (
                                          <div className="flex items-center gap-2">
                                            <input type="checkbox" id={`closed-${i}-${dayIndex}`} checked={field.value} onChange={field.onChange} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                            <label htmlFor={`closed-${i}-${dayIndex}`} className="text-xs cursor-pointer text-muted-foreground">Closed</label>
                                          </div>
                                        )}
                                      />
                                    </div>
                                    {!watch(`locations.${i}.schedules.${dayIndex}.isClosed`) && (
                                      <div className="flex flex-1 items-center gap-2">
                                        <Controller
                                          control={control} name={`locations.${i}.schedules.${dayIndex}.startTime`}
                                          render={({ field }) => (
                                            <Select value={field.value} onValueChange={field.onChange}>
                                              <SelectTrigger className="w-full text-xs h-9"><SelectValue placeholder="Start Time" /></SelectTrigger>
                                              <SelectContent className="max-h-48">
                                                {TIME_OPTIONS.map(time => <SelectItem key={time} value={time} className="text-xs">{time}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                          )}
                                        />
                                        <span className="text-muted-foreground text-xs">to</span>
                                        <Controller
                                          control={control} name={`locations.${i}.schedules.${dayIndex}.endTime`}
                                          render={({ field }) => (
                                            <Select value={field.value} onValueChange={field.onChange}>
                                              <SelectTrigger className="w-full text-xs h-9"><SelectValue placeholder="End Time" /></SelectTrigger>
                                              <SelectContent className="max-h-48">
                                                {TIME_OPTIONS.map(time => <SelectItem key={time} value={time} className="text-xs">{time}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                          )}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button
                      type="button" variant="outline"
                      className="w-full rounded-xl border-dashed h-11 text-sm"
                      onClick={() => addLoc({
                        isPrimary: false, buildingNo: "", address1: "", address2: "", city: "", state: "",
                        zipCode: "", phone: "", officePhone: "", fax: "", email: "", website: "", schedules: [...DEFAULT_SCHEDULES],
                      })}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Another Location
                    </Button>
                  </div>
                )}

                {/* ── Step 4: Online Presence ── */}
                {step === 4 && (
                  <div className="space-y-6">
                    <SectionHeader icon={Globe} title="Online Presence" subtitle="Link your profiles to your account" />

                    {/* CPT Codes */}
                    <div className="space-y-3 p-4 border rounded-xl bg-slate-50/50">
                      <div>
                        <Label className="text-base font-semibold">Assign CPT Codes</Label>
                        <p className="text-sm text-muted-foreground">Select master CPT codes to assign to this provider.</p>
                      </div>
                      <Controller
                        control={control}
                        name="cptCodes"
                        render={({ field }) => (
                          <MultiSelect
                            placeholder="Select CPT Codes..."
                            options={(masterCptCodes || []).map((c: any) => ({ value: String(c.id), label: `${c.code} - ${c.codeShortDesc || c.description} ($${c.standardPrice})` }))}
                            value={field.value || []}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>

                    {/* Profile Links */}
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">External Profile Links</p>
                      <p className="text-xs text-muted-foreground -mt-2">Paste full URLs to your existing profiles on other platforms</p>
                      {[
                        { name: "zocdocUrl" as const, label: "ZocDoc Profile", placeholder: "https://www.zocdoc.com/provider/...", color: "bg-blue-500" },
                        { name: "webmdUrl" as const, label: "WebMD Profile", placeholder: "https://provider.webmd.com/provider/...", color: "bg-orange-500" },
                        { name: "healthgradesUrl" as const, label: "Healthgrades Profile", placeholder: "https://www.healthgrades.com/physician/...", color: "bg-green-600" },
                        { name: "usnewsUrl" as const, label: "US News Health Profile", placeholder: "https://health.usnews.com/providers/...", color: "bg-red-600" },
                      ].map(({ name, label, placeholder, color }) => (
                        <div key={name} className="flex items-center gap-3">
                          <div className={`w-2 h-10 rounded-full ${color} flex-shrink-0`} />
                          <div className="flex-1">
                            <FieldLabel>{label}</FieldLabel>
                            <Input {...register(name)} placeholder={placeholder} className="mt-1 text-sm" />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                      <p className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">Profile Summary</p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div className="text-muted-foreground">Name</div>
                        <div className="font-medium truncate">{watch("displayName") || "—"}</div>
                        <div className="text-muted-foreground">NPI</div>
                        <div className="font-medium">{watch("npiNumber") || "—"}</div>
                        <div className="text-muted-foreground">Specialties</div>
                        <div className="font-medium">{watch("providerTypes").length > 0 ? watch("providerTypes").length + " selected" : "—"}</div>
                        <div className="text-muted-foreground">Locations</div>
                        <div className="font-medium">{watch("locations").length}</div>
                        <div className="text-muted-foreground">Credentials</div>
                        <div className="font-medium">{watch("credentials").length}</div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</div>

              <div className="flex items-center gap-3">
                <Button
                  type="button" variant="outline"
                  className="rounded-xl h-11 px-6"
                  onClick={step === 0 ? () => setLocation("/login") : goBack}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {step === 0 ? "Back to Login" : "Back"}
                </Button>

                {step < STEPS.length - 1 ? (
                  <Button
                    type="button"
                    className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                    onClick={goNext}
                    disabled={!isStepValid()}
                  >
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                    onClick={onSubmit}
                    disabled={loading || !isStepValid()}
                  >
                    {loading ? "Submitting…" : <><Check className="w-4 h-4 mr-1.5" />Submit Registration</>}
                  </Button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </ProviderLayout>
  );
}

/* ─── SectionHeader ──────────────────────────────────────────────────────── */

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
